import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    IconButton, 
    TextField,
    InputAdornment,
    CircularProgress,
    Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import employeeServiceExtended from '../../services/employeeServiceExtended';
import EmployeesTable from './EmployeesTable';

const EmployeesPage = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);

    // Load employees from IndexedDB
    const loadEmployees = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await employeeServiceExtended.getEmployees();
            setEmployees(data);
        } catch (err) {
            console.error('Error loading employees:', err);
            setError('Failed to load employees. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Sync employees from server (hidden but functional)
    const syncEmployees = async () => {
        try {
            setSyncing(true);
            setError(null);
            await employeeServiceExtended.syncEmployees();
            await loadEmployees();
        } catch (err) {
            console.error('Error syncing employees:', err);
            setError('Failed to sync employees. Please check your connection.');
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        loadEmployees();
    }, []);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'background.default'
        }}>
            {/* Header */}
            <Box 
                sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    flex: '0 0 auto'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/main')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Employees
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            Total: {employees.length} employees
                        </Typography>
                    </Box>
                </Box>
                
                {/* Sync button hidden but functionality kept */}
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert 
                    severity="error" 
                    onClose={() => setError(null)}
                    sx={{ mx: 2, mt: 2, flex: '0 0 auto' }}
                >
                    {error}
                </Alert>
            )}

            {/* Search Bar */}
            <Box sx={{ px: 2, py: 2, flex: '0 0 auto' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by name, phone or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        }
                    }}
                />
            </Box>

            {/* Employees Table */}
            <Box sx={{ flex: '1 1 auto', px: 2, pb: 2, overflow: 'hidden' }}>
                {employees.length === 0 ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        gap: 2
                    }}>
                        <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                        <Typography variant="h6" color="text.secondary">
                            No employees found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Employee data will be synchronized automatically
                        </Typography>
                    </Box>
                ) : (
                    <EmployeesTable 
                        employees={employees} 
                        searchTerm={searchTerm}
                    />
                )}
            </Box>
        </Box>
    );
};

export default EmployeesPage;